/**
 * Phones handed to brokers.
 *
 * Handing one out takes it off the shop's sellable count and taking it back puts it on again, so
 * both invalidate stock as well as the list — the till's "N in stock" must drop the moment the
 * broker walks out with the phone, not at the next catalogue refresh.
 */

import { api } from '../api.js';
import { brokerLoanListTag, stockListTag, stockTag } from '../tags.js';
import type { ShopId, UnitId } from '../../types/api.js';
import type { BrokerId, BrokerLoan, BrokerLoanId } from '../../types/oversight.js';

export const brokerLoansApi = api.injectEndpoints({
  endpoints: (build) => ({
    listBrokerLoans: build.query<
      BrokerLoan[],
      { shopId?: ShopId | undefined; includeClosed?: boolean | undefined }
    >({
      query: (params) => ({ url: '/broker-loans', params }),
      providesTags: [brokerLoanListTag()],
    }),

    lendUnitToBroker: build.mutation<
      BrokerLoanId,
      {
        shopId: ShopId;
        inventoryUnitId: UnitId;
        brokerId: BrokerId;
        /** `YYYY-MM-DD`. */
        dueBackOn: string;
        note?: string | undefined;
      }
    >({
      query: (body) => ({ url: '/broker-loans', method: 'POST', body }),
      invalidatesTags: [brokerLoanListTag(), stockListTag()],
    }),

    /** `sold: true` when the broker brought the money — the phone comes back only to be rung up. */
    returnUnitFromBroker: build.mutation<BrokerLoan, { id: BrokerLoanId; sold: boolean }>({
      query: ({ id, sold }) => ({
        url: `/broker-loans/${id}/return`,
        method: 'POST',
        body: { sold },
      }),
      invalidatesTags: (result) => [
        brokerLoanListTag(),
        stockListTag(),
        ...(result === undefined ? [] : [stockTag(result.shopId, result.variantId)]),
      ],
    }),
  }),
});

export const {
  useListBrokerLoansQuery,
  useLendUnitToBrokerMutation,
  useReturnUnitFromBrokerMutation,
} = brokerLoansApi;
